/**
 * Price-override list dialog shared by the device and feature pricing
 * config grids — shows every site-group/site override for one pricing
 * row, with inline edit and an entry point to add a new override.
 */
import { Add, Delete, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddPricingOverrideDialog from '@sophocles/views/billing/dialogs/AddPricingOverrideDialog';

function OverridesDialog({
  open,
  onClose,
  rowKey,
  entityLabel,
  configs,
  getRowKey,
  editMode,
  onAddOverride,
  onRemoveOverride,
  onUpdateOverride,
  customerId,
}) {
  const { t } = useTranslation();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Get config from Redux state (via configs prop) so it updates in real-time
  const config = useMemo(() => configs.find(c => getRowKey(c) === rowKey), [configs, getRowKey, rowKey]);

  // Filter overrides by search query
  const filteredOverrides = useMemo(() => {
    if (!config?.overrides || !searchQuery.trim()) {
      return config?.overrides || [];
    }
    const query = searchQuery.toLowerCase();
    return config.overrides.filter(override => {
      const name = override.site_group?.name || override.site?.name || '';
      return name.toLowerCase().includes(query);
    });
  }, [config?.overrides, searchQuery]);

  // Reset search when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  const handleAddOverride = useCallback(
    override => {
      onAddOverride(rowKey, override);
      setAddDialogOpen(false);
    },
    [rowKey, onAddOverride],
  );

  if (!config) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('billing.pricingConfig.priceOverridesTitle', { name: entityLabel(config) })}</DialogTitle>
        <DialogContent>
          {config.overrides?.length > 0 && (
            <TextField
              size="small"
              hiddenLabel
              placeholder={t('billing.pricingConfig.searchOverrides')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              fullWidth
              sx={{ mt: 1, mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          {filteredOverrides.length > 0 ? (
            <Box>
              {filteredOverrides.map((override, index) => {
                // Find the actual index in the original array for removal
                const originalIndex = config.overrides.indexOf(override);
                return (
                  <Box
                    key={`${override.site_group?.id || override.site?.id}-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      py: 1.5,
                      px: 2,
                      mb: 1,
                      bgcolor: 'grey.50',
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    <Typography sx={{ flex: 1 }}>
                      {override.site_group?.name ||
                        override.site?.name ||
                        t('billing.pricingConfig.unknownOverrideTarget')}
                    </Typography>
                    <Chip
                      label={
                        override.site_group
                          ? t('billing.pricingConfig.overrideTypeSiteGroup')
                          : t('billing.pricingConfig.overrideTypeSite')
                      }
                      size="small"
                      variant="outlined"
                    />
                    {editMode ? (
                      <TextField
                        size="small"
                        hiddenLabel
                        type="number"
                        value={override.price}
                        onChange={e => onUpdateOverride(rowKey, originalIndex, { price: e.target.value })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{ step: '0.01', min: '0' }}
                        sx={{ width: 120 }}
                      />
                    ) : (
                      <Typography sx={{ fontWeight: 600 }}>${parseFloat(override.price).toFixed(2)}</Typography>
                    )}
                    {editMode && (
                      <IconButton size="small" onClick={() => onRemoveOverride(rowKey, originalIndex)} color="error">
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : config.overrides?.length > 0 ? (
            <Typography color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
              {t('billing.pricingConfig.noOverridesMatchSearch')}
            </Typography>
          ) : (
            <Typography color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
              {t('billing.pricingConfig.noOverridesConfigured')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {editMode && config.pricing_unit && (
            <Button startIcon={<Add />} onClick={() => setAddDialogOpen(true)}>
              {t('billing.pricingConfig.addOverride')}
            </Button>
          )}
          <Button onClick={onClose}>{t('billing.pricingConfig.close')}</Button>
        </DialogActions>
      </Dialog>

      <AddPricingOverrideDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddOverride}
        customerId={customerId}
        entityName={entityLabel(config)}
        existingOverrides={config.overrides || []}
      />
    </>
  );
}

export default OverridesDialog;
